import type { CSSProperties } from 'react'
import { UserAvatar } from '@/graphql/schema'
import { normalizePresenceAvatar } from '@/features/workspace-presence/model/presence-avatar'
import { cn } from '@/shared/lib/utils'

const SPRITE_COLUMNS = 4
const TILE_SIZE = 256

const spriteAvatarGroups = [
  [
    UserAvatar.SupportAgentBlue,
    UserAvatar.CurlyGlassesYellow,
    UserAvatar.HoodieCapRed,
    UserAvatar.BunGlassesRed,
    UserAvatar.BeanieBeardGreen,
    UserAvatar.GamerHeadsetPurple,
    UserAvatar.DefaultSilhouetteBlue,
    UserAvatar.ServiceRobotCyan,
  ],
  [
    UserAvatar.WomanBunCream,
    UserAvatar.ManGlassesCharcoal,
    UserAvatar.WomanLongHairWhite,
    UserAvatar.HoodieBeardBlue,
    UserAvatar.PinkBobBlack,
    UserAvatar.ManGlassesBeige,
    UserAvatar.WomanChestnutGreen,
    UserAvatar.BeanieBeardBlack,
  ],
  [
    UserAvatar.ShibaAstronaut,
    UserAvatar.CatMoonwalker,
    UserAvatar.PenguinRocket,
    UserAvatar.FrogUfo,
    UserAvatar.OwlPilot,
    UserAvatar.RaccoonSpaceRanger,
    UserAvatar.RabbitRover,
    UserAvatar.CrabAviator,
  ],
] as const satisfies ReadonlyArray<ReadonlyArray<UserAvatar>>

const spriteSources = [
  '/user-avatars/avatar-sprite-people.png',
  '/user-avatars/avatar-sprite-humans.png',
  '/user-avatars/avatar-sprite-animals.png',
] as const

export type AvatarSpriteSpec = {
  avatar: UserAvatar
  column: number
  row: number
  spriteSrc: string
  tileX: number
  tileY: number
  offsetX: number
  offsetY: number
}

const avatarSpriteOffsets = {
  [UserAvatar.SupportAgentBlue]: { offsetX: -16, offsetY: -204 },
  [UserAvatar.CurlyGlassesYellow]: { offsetX: -6, offsetY: -211 },
  [UserAvatar.HoodieCapRed]: { offsetX: 1, offsetY: -215 },
  [UserAvatar.BunGlassesRed]: { offsetX: 7, offsetY: -215 },
  [UserAvatar.BeanieBeardGreen]: { offsetX: -17, offsetY: -289 },
  [UserAvatar.GamerHeadsetPurple]: { offsetX: -7, offsetY: -263 },
  [UserAvatar.DefaultSilhouetteBlue]: { offsetX: -1, offsetY: -281 },
  [UserAvatar.ServiceRobotCyan]: { offsetX: 6, offsetY: -285 },
  [UserAvatar.WomanBunCream]: { offsetX: -18, offsetY: -226 },
  [UserAvatar.ManGlassesCharcoal]: { offsetX: -2, offsetY: -226 },
  [UserAvatar.WomanLongHairWhite]: { offsetX: 4, offsetY: -219 },
  [UserAvatar.HoodieBeardBlue]: { offsetX: 21, offsetY: -219 },
  [UserAvatar.PinkBobBlack]: { offsetX: -15, offsetY: -248 },
  [UserAvatar.ManGlassesBeige]: { offsetX: -3, offsetY: -244 },
  [UserAvatar.WomanChestnutGreen]: { offsetX: 2, offsetY: -248 },
  [UserAvatar.BeanieBeardBlack]: { offsetX: 18, offsetY: -248 },
  [UserAvatar.ShibaAstronaut]: { offsetX: -19, offsetY: -189 },
  [UserAvatar.CatMoonwalker]: { offsetX: -9, offsetY: -193 },
  [UserAvatar.PenguinRocket]: { offsetX: 5, offsetY: -193 },
  [UserAvatar.FrogUfo]: { offsetX: 6, offsetY: -196 },
  [UserAvatar.OwlPilot]: { offsetX: -18, offsetY: -322 },
  [UserAvatar.RaccoonSpaceRanger]: { offsetX: -12, offsetY: -315 },
  [UserAvatar.RabbitRover]: { offsetX: 0, offsetY: -319 },
  [UserAvatar.CrabAviator]: { offsetX: 0, offsetY: -319 },
} satisfies Record<UserAvatar, { offsetX: number; offsetY: number }>

export const avatarSpriteGrid = spriteAvatarGroups.flatMap(
  (avatars, spriteIndex) =>
    avatars.map((avatar, tileIndex) => ({
      avatar,
      column: spriteIndex,
      row: tileIndex,
      spriteSrc: spriteSources[spriteIndex],
      tileX: tileIndex % SPRITE_COLUMNS,
      tileY: Math.floor(tileIndex / SPRITE_COLUMNS),
      ...avatarSpriteOffsets[avatar],
    })),
) satisfies Array<AvatarSpriteSpec>

export const avatarSpriteMap = Object.fromEntries(
  avatarSpriteGrid.map((spec) => [spec.avatar, spec]),
) as Record<UserAvatar, AvatarSpriteSpec>

const fallbackAvatarSpriteSpec = avatarSpriteMap[UserAvatar.WomanChestnutGreen]

export function getAvatarLabel(avatar: UserAvatar) {
  return avatar
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function getAvatarSpriteStyle(
  avatar: UserAvatar,
  size: number,
): CSSProperties {
  const normalizedAvatar = normalizePresenceAvatar(avatar)
  const spriteSpec = normalizedAvatar
    ? avatarSpriteMap[normalizedAvatar]
    : fallbackAvatarSpriteSpec
  const { offsetX, offsetY, spriteSrc, tileX, tileY } = spriteSpec
  const scale = size / TILE_SIZE
  const backgroundPositionX = (-tileX * TILE_SIZE + offsetX) * scale
  const backgroundPositionY = (-tileY * TILE_SIZE + offsetY) * scale

  return {
    backgroundImage: `url("${spriteSrc}")`,
    backgroundPosition: `${backgroundPositionX}px ${backgroundPositionY}px`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: `${(1024 / TILE_SIZE) * size}px ${(1024 / TILE_SIZE) * size}px`,
    height: size,
    width: size,
  }
}

export function AvatarSprite({
  avatar,
  className,
  size,
}: {
  avatar: UserAvatar
  className?: string
  size: number
}) {
  return (
    <span
      aria-hidden="true"
      className={cn('block rounded-xl', className)}
      style={getAvatarSpriteStyle(avatar, size)}
    />
  )
}
